
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { profileService } from "@/services/profileService";

interface FormData {
  display_name: string;
  bio: string;
  date_of_birth: string;
  show_age: boolean;
  height: string;
  weight: string;
  body_type: string;
  position: string;
  hierarchy: string;
  turned_on_by: string[];
  age_range_min: string;
  age_range_max: string;
  address: string;
  city: string;
  additional_info: string;
}

// Helper function to convert DD.MM.YYYY to YYYY-MM-DD for calculations
const formatDateForCalculation = (ddmmyyyy: string): string => {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return "";
  const [day, month, year] = ddmmyyyy.split('.');
  if (!day || !month || !year || day.length !== 2 || month.length !== 2 || year.length !== 4) return "";
  return `${year}-${month}-${day}`;
};

// Helper function to convert YYYY-MM-DD to DD.MM.YYYY for display
const formatDateForDisplay = (yyyymmdd: string): string => {
  if (!yyyymmdd) return "";
  const [year, month, day] = yyyymmdd.split('-');
  if (!day || !month || !year) return "";
  return `${day}.${month}.${year}`;
};

// Helper function to calculate age from date of birth in DD.MM.YYYY format
const calculateAge = (dateOfBirth: string): number => {
  if (!dateOfBirth) return 0;
  const isoDate = formatDateForCalculation(dateOfBirth);
  if (!isoDate) return 0;
  
  const today = new Date();
  const birthDate = new Date(isoDate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Helper function to convert age back to approximate date of birth in DD.MM.YYYY format
const ageToDateOfBirth = (age: number): string => {
  if (!age) return "";
  const today = new Date();
  const birthYear = today.getFullYear() - age;
  return `01.01.${birthYear}`; // Use January 1st as default in DD.MM.YYYY format
};

export const useCompleteProfile = (refetchProfileCompletion?: () => Promise<void>) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [photos, setPhotos] = useState<string[]>([]);
  const [privateInterests, setPrivateInterests] = useState<string[]>([]);
  const [publicInterests, setPublicInterests] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({
    display_name: "",
    bio: "",
    date_of_birth: "",
    show_age: true,
    height: "",
    weight: "",
    body_type: "",
    position: "",
    hierarchy: "",
    turned_on_by: [],
    age_range_min: "18",
    age_range_max: "99",
    address: "",
    city: "",
    additional_info: ""
  });

  useEffect(() => {
    if (user) {
      loadExistingProfile();
    }
  }, [user]);

  const loadExistingProfile = async () => {
    if (!user) return;

    try {
      setLoadingProfile(true);
      console.log('🔍 Loading profile for user:', user.id);
      
      const existingProfile = await profileService.loadExistingProfile(user.id);

      if (existingProfile) {
        console.log('✅ Profile found, populating form data');
        setIsFirstTime(false);
        setFormData({
          display_name: existingProfile.display_name || "",
          bio: existingProfile.bio || "",
          date_of_birth: existingProfile.age ? ageToDateOfBirth(existingProfile.age) : "",
          show_age: existingProfile.show_age !== undefined ? existingProfile.show_age : true,
          height: existingProfile.height ? existingProfile.height.toString() : "",
          weight: existingProfile.weight ? existingProfile.weight.toString() : "",
          body_type: existingProfile.body_type || "",
          position: existingProfile.position || "",
          hierarchy: existingProfile.hierarchy || "",
          turned_on_by: existingProfile.turned_on_by || [],
          age_range_min: existingProfile.age_range_min ? existingProfile.age_range_min.toString() : "18",
          age_range_max: existingProfile.age_range_max ? existingProfile.age_range_max.toString() : "99",
          address: existingProfile.address || "",
          city: existingProfile.city || "",
          additional_info: existingProfile.additional_info || ""
        });

        setPhotos(existingProfile.photo_urls || existingProfile.photos || []);
        setPrivateInterests(existingProfile.private_interests || []);
        setPublicInterests(existingProfile.public_interests || []);
      } else {
        console.log('ℹ️ No existing profile found - this is normal for new users');
        setIsFirstTime(true);
        // For new users (especially Google OAuth), pre-populate display_name if available
        if (user.user_metadata?.full_name || user.user_metadata?.name) {
          setFormData(prev => ({
            ...prev,
            display_name: user.user_metadata?.full_name || user.user_metadata?.name || ""
          }));
        }
        
        // Pre-populate photo if available from OAuth
        if (user.user_metadata?.avatar_url || user.user_metadata?.picture) {
          setPhotos([user.user_metadata?.avatar_url || user.user_metadata?.picture]);
        }
      }
    } catch (error: any) {
      console.error('❌ Error loading existing profile:', error);
      // Only show error toast for actual errors, not missing profiles
      if (error.message && !error.message.includes('No rows returned')) {
        toast({
          title: "Error",
          description: "Failed to load existing profile data",
          variant: "destructive",
        });
      } else {
        console.log('ℹ️ Profile not found - user needs to complete profile setup');
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    console.log('🚀 Complete Profile button clicked!');
    
    // Check if user is at least 18 years old
    const age = calculateAge(formData.date_of_birth);
    if (age < 18) {
      toast({
        title: "Age requirement not met",
        description: "You must be at least 18 years old to use this app.",
        variant: "destructive",
      });
      return;
    }

    console.log('📊 Form validation status:', {
      hasDisplayName: !!formData.display_name,
      hasDateOfBirth: !!formData.date_of_birth,
      calculatedAge: age,
      privateInterestsCount: privateInterests.length,
      isFormValid: formData.display_name && formData.date_of_birth && privateInterests.length >= 5
    });

    if (privateInterests.length < 5) {
      console.log('❌ Not enough private interests:', privateInterests.length);
      toast({
        title: "Not enough private interests",
        description: "Please add at least 5 private interests to help us find better matches for you.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const updateData: any = {
        is_profile_complete: true
      };

      if (formData.display_name.trim()) {
        updateData.display_name = formData.display_name.trim();
      }
      
      if (formData.bio.trim()) {
        updateData.bio = formData.bio.trim();
      }
      
      if (formData.date_of_birth) {
        updateData.age = age; // Store calculated age in the existing age field
      }

      updateData.show_age = formData.show_age;
      
      if (formData.height && !isNaN(parseInt(formData.height))) {
        updateData.height = parseInt(formData.height);
      }

      if (formData.weight && !isNaN(parseInt(formData.weight))) {
        updateData.weight = parseInt(formData.weight);
      }

      if (formData.body_type) {
        updateData.body_type = formData.body_type;
      }

      if (formData.position) {
        updateData.position = formData.position;
      }

      if (formData.hierarchy) {
        updateData.hierarchy = formData.hierarchy;
      }

      updateData.turned_on_by = formData.turned_on_by;
      updateData.private_interests = privateInterests;
      updateData.public_interests = publicInterests;
      updateData.photo_urls = photos;
      updateData.photos = photos;
      
      if (formData.age_range_min && !isNaN(parseInt(formData.age_range_min))) {
        updateData.age_range_min = parseInt(formData.age_range_min);
      }
      
      if (formData.age_range_max && !isNaN(parseInt(formData.age_range_max))) {
        updateData.age_range_max = parseInt(formData.age_range_max);
      }

      // Add address and city fields
      if (formData.address.trim()) {
        updateData.address = formData.address.trim();
      }

      if (formData.city.trim()) {
        updateData.city = formData.city.trim();
      }

      if (formData.additional_info.trim()) {
        updateData.additional_info = formData.additional_info.trim();
      }

      console.log('Updating profile with data:', updateData);
      await profileService.updateProfile(user.id, updateData);

      console.log('Profile update successful, refreshing profile completion status...');
      
      // Trigger a refetch of profile completion status if available
      if (refetchProfileCompletion) {
        console.log('🔄 Calling refetchProfileCompletion to sync state');
        await refetchProfileCompletion();
        console.log('✅ Profile completion status refreshed');
      }

      toast({
        title: "Profile Complete! ✅",
        description: "Welcome to Alpha_v8 Dating. Let's find your perfect match!",
      });

      // Navigate after a short delay to ensure state is updated
      setTimeout(() => {
        console.log('🚀 Navigating to discovery page');
        navigate('/discovery', { replace: true });
      }, 200);
      
    } catch (error: any) {
      console.error('Profile completion failed:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.display_name && formData.date_of_birth && privateInterests.length >= 5;

  // Add debugging for form validation
  console.log('🔍 Form validation debug:', {
    displayName: formData.display_name,
    dateOfBirth: formData.date_of_birth,
    calculatedAge: calculateAge(formData.date_of_birth),
    privateInterestsCount: privateInterests.length,
    isFormValid,
    loading
  });

  return {
    user,
    loading,
    loadingProfile,
    isFirstTime,
    photos,
    setPhotos,
    privateInterests,
    setPrivateInterests,
    publicInterests,
    setPublicInterests,
    formData,
    handleInputChange,
    handleSubmit,
    isFormValid
  };
};
